import React, { useEffect } from "react";
import styled from "styled-components";
import { NavLink, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import * as AuthenticationStore from "../store/AuthenticationReducer";
import * as actions from "./Utils/actions";
import { LogoutIcon, ReportsIcon, ClipboardListIcon } from "../svg";
import ShoppingBag from "svg/ShoppingBag";
import Dropdown from "components/Dropdown";
import { Dropdown as AntDropdown } from "antd/lib";
import i18next from "i18next";
import DropdownLanguage from "components/DropdownLanguage";
import { useTranslation } from "react-i18next";
import { Avatar } from "antd/lib";
import { MenuProps } from "antd";
import Cookies from "universal-cookie";

const Logo = styled.a`
  cursor: pointer;
  display: block;
  width: 110px;

  img {
    width: 100%;
    display: block;
  }
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  padding: 0 30px;
  box-shadow: 0 4px 30px -4px rgba(0, 51, 90, 0.1);
  background: #fff;
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: space-between;
  z-index: 80;
`;

const Nav = styled.ul`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  align-items: center;

  & > div {
    display: flex;
    align-items: center;
  }
`;

Nav.Item = styled.li`
  position: relative;
  display: inline-block;
  margin: 0 15px;

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }

  > a {
    color: ${({ theme }) => theme.colors.dark};
    display: flex;
    align-items: center;
    font-size: 12px;
    padding: 18px 0;
    font-weight: 500;
    text-transform: uppercase;

    svg {
      width: 20px;
      height: 20px;
      display: block;
      fill: ${({ theme }) => theme.colors.dark};
    }

    &:hover,
    &.active {
      color: ${({ theme }) => theme.colors.default};

      svg {
        fill: ${({ theme }) => theme.colors.default};
      }
    }
  }
`;

const AvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  div {
    margin-left: 16px;
    color: #5c5c5c;
    display: flex;
    flex-direction: column;
    gap: 5px;

    h3 {
      font-weight: 700;
      font-size: 18px;
    }

    p {
      font-weight: 400;
      font-size: 14px;
    }
  }
`;

const StyledNavLink = styled(NavLink)`
  color: #8a8a8a;
  font-weight: bold;
  font-size: 16px;
`;

const IconWrapper = styled.div`
  background: #efefef;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`;

function NavMenu() {
  const cookies = new Cookies();
  const isAuthenticated = useSelector(
    (state: any) => state.authentication.isAuthenticated
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  useEffect(() => {
    const userRole = actions.getUserRole();
    if (userRole) {
      dispatch(AuthenticationStore.actionCreators.changeAuthentication(true));
    }
  }, [dispatch]);

  useEffect(() => {
    const userRole = actions.getUserRole();
    if (isAuthenticated && !userRole) {
      dispatch(AuthenticationStore.actionCreators.changeAuthentication(false));
      navigate("/");
    }
  }, [isAuthenticated, dispatch, navigate]);

  const logout = (event) => {
    event.preventDefault();
    dispatch(AuthenticationStore.actionCreators.changeAuthentication(false));
    navigate("/");
  };

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <NavLink to="/logout" onClick={logout} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 18, height: 24, marginRight: 10 }}>
            <LogoutIcon />
          </div>
          {i18next.t("logout")}
        </NavLink>
      ),
    },
  ];

  return isAuthenticated ? (
    <Header>
      <Nav>
        <div>
          <Nav.Item>
            <StyledNavLink to="/financial-info">
              <IconWrapper>
                <ReportsIcon />
              </IconWrapper>
              {i18next.t("financial_info")}
            </StyledNavLink>
          </Nav.Item>

          <Nav.Item>
            <StyledNavLink to="/logistics-info">
              <IconWrapper>
                <ClipboardListIcon />
              </IconWrapper>
              {i18next.t("title_logistics_info")}
            </StyledNavLink>
          </Nav.Item>

          <Nav.Item>
            <Dropdown
              icon={ShoppingBag}
              title={i18next.t("order")}
              items={[
                { to: "/create-order", label: i18next.t("create") },
                { to: "/order-history", label: i18next.t("history") },
              ]}
            />
          </Nav.Item>

          <Nav.Item>
            <StyledNavLink to="/upload-data">
              <IconWrapper>
                <ReportsIcon />
              </IconWrapper>
              {i18next.t("upload_data")}
            </StyledNavLink>
          </Nav.Item>

          <Nav.Item>
            <StyledNavLink to="/sent-data">
              <IconWrapper>
                <ReportsIcon />
              </IconWrapper>
              {i18next.t("sent_data")}
            </StyledNavLink>
          </Nav.Item>

          <Nav.Item>
            <StyledNavLink to="/poc-orders">
              <IconWrapper>
                <ReportsIcon />
              </IconWrapper>
              {i18next.t("poc_orders")}
            </StyledNavLink>
          </Nav.Item>
        </div>

        <div>
          <Nav.Item>
            <IconWrapper>
              <DropdownLanguage />
            </IconWrapper>
          </Nav.Item>

          <Nav.Item>
            <AntDropdown trigger={["click"]} menu={{ items }}>
              <AvatarWrapper>
                <Avatar size="large">
                  {cookies.get("token")["UserName"][0] ?? "D"}
                </Avatar>
                <div>
                  <h3>{cookies.get("token")["UserName"] ?? "D"}</h3>
                  <p>{i18next.t("distributor")}</p>
                </div>
              </AvatarWrapper>
            </AntDropdown>
          </Nav.Item>
        </div>
      </Nav>
    </Header>
  ) : null;
}

export default NavMenu;
